import React from 'react';
import { ListingKind, ListingPictureKind, MediaType } from '@kouto/types';
import { useAppState } from 'AppProvider';
import { ListingSeoMeta } from 'components/molecules/ListingSeoMeta';
import useCanonicalBase from 'hooks/useCanonicalBase';
import { buildCanonicalPath, useCanonicalHref } from 'hooks/useCanonicalLink';
import type { PublicListingDetailsV3 } from 'types/listingV3';
import { getBiggerMediaLink } from 'utils/listings';
import {
  buildCollectionJsonLd,
  buildEventJsonLd,
} from 'utils/seo/buildListingJsonLd';

export interface ListingSeoProps {
  listing: PublicListingDetailsV3;
}

type ListingMedia = Parameters<typeof getBiggerMediaLink>[0];

/** The listing's cover image, else the brand's hero image, so OG / JSON-LD always carry one. */
const deriveSeoImage = (
  listing: PublicListingDetailsV3,
  heroMedia: ListingMedia | undefined,
): string | undefined => {
  const cover = listing.medias.find(
    (media) =>
      media.kind === ListingPictureKind.COVER && media.type === MediaType.IMAGE,
  );
  if (cover) return getBiggerMediaLink(cover);
  return heroMedia?.type === MediaType.IMAGE
    ? getBiggerMediaLink(heroMedia)
    : undefined;
};

/**
 * SEO tags for a collection or event detail page — the counterpart of `EventMeta` for experiences.
 * The canonical uses the slug (falling back to the collection UUID) on the brand's canonical base, so
 * it is the same URL the sitemap lists for this listing.
 */
const ListingSeo = ({ listing }: ListingSeoProps) => {
  const { settings } = useAppState(
    (state: Record<string, unknown>) => state.brand,
  );
  const brandName = settings?.name as string | undefined;
  const heroMedia = settings?.templateElement?.hero?.desktopMedia as
    | ListingMedia
    | undefined;

  const routeId = listing.slug ?? listing.resourceGroupCollectionId;
  const isEvent = listing.kind === ListingKind.EVENT;
  const path = buildCanonicalPath(
    isEvent ? { eventId: routeId ?? undefined } : { collectionId: routeId },
  );
  const canonicalUrl = useCanonicalHref(path);
  const [brandUrl] = useCanonicalBase().split('?');

  if (!canonicalUrl) return null;

  const image = deriveSeoImage(listing, heroMedia);
  const jsonLdParams = {
    brandName,
    brandUrl,
    image,
    listing,
    url: canonicalUrl,
  };
  const jsonLd = isEvent
    ? buildEventJsonLd(jsonLdParams)
    : buildCollectionJsonLd(jsonLdParams);

  return (
    <ListingSeoMeta
      brandName={brandName}
      canonicalUrl={canonicalUrl}
      description={listing.description || listing.summary}
      image={image}
      jsonLd={jsonLd}
      title={listing.title}
    />
  );
};

export default ListingSeo;
